import { ButtonHTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

import { palettes, space, tokens } from '../theme/tokens';
import { Avatar, UserAvatarView } from './Avatar';
import { StatusIndicator, StatusIndicatorState } from './StatusIndicator';

export type PropertyChipType = 'icon' | 'status' | 'user';

type PropertyChipProps = {
	label: string;
	type?: PropertyChipType;
	icon?: ReactNode;
	status?: StatusIndicatorState;
	user?: UserAvatarView;
	disabled?: boolean;
	onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
	'data-preview'?: 'hover' | 'pressed';
};

const Container = styled.button<{ $disabled?: boolean; $clickable?: boolean }>`
	display: inline-flex;
	align-items: center;
	box-sizing: border-box;
	height: 30px;
	margin: 0;
	padding: 0 ${tokens.padding.control}px;
	gap: ${tokens.gap.inline}px;
	border: 1px solid
		${(props) => (props.$disabled ? palettes.heatherGray['200'] : tokens.border.subtle)};
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.surface.white};
	box-shadow: ${tokens.shadow.button};
	color: ${(props) => (props.$disabled ? tokens.text.disabled : tokens.text.primary)};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: ${tokens.typography.weights.medium};
	line-height: ${tokens.typography.lineHeights.heading};
	letter-spacing: 0.14px;
	white-space: nowrap;
	cursor: ${(props) => (props.$clickable && !props.$disabled ? 'pointer' : 'default')};
	appearance: none;

	&:hover,
	&[data-preview='hover'] {
		background: ${(props) => (props.$disabled ? tokens.surface.white : tokens.surface.hover)};
	}

	&:active,
	&[data-preview='pressed'] {
		background: ${tokens.surface.white};
	}

	&:disabled {
		cursor: default;
	}
`;

const Leading = styled.span<{ $faded?: boolean; $icon?: boolean }>`
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	color: ${(props) => (props.$icon ? tokens.icon.tertiary : 'inherit')};
	opacity: ${(props) => (props.$faded ? 0.5 : 1)};

	svg {
		display: block;
		width: ${space.md}px;
		height: ${space.md}px;
		color: inherit;
	}
`;

function resolveType(props: PropertyChipProps): PropertyChipType {
	if (props.type) {
		return props.type;
	}

	if (props.user) {
		return 'user';
	}

	if (props.status !== undefined) {
		return 'status';
	}

	return 'icon';
}

export function PropertyChip(props: PropertyChipProps) {
	const type = resolveType(props);
	const disabled = props.disabled ?? false;
	const isButton = Boolean(props.onClick);

	return (
		<Container
			as={isButton ? 'button' : 'span'}
			type={isButton ? 'button' : undefined}
			disabled={isButton ? disabled : undefined}
			$disabled={disabled}
			$clickable={isButton}
			aria-disabled={!isButton && disabled ? true : undefined}
			onClick={isButton && !disabled ? props.onClick : undefined}
			data-preview={props['data-preview']}
		>
			<Leading $faded={disabled && type !== 'icon'} $icon={type === 'icon' && !disabled}>
				{type === 'user' && props.user && <Avatar user={props.user} size={16} />}
				{type === 'status' && <StatusIndicator state={props.status} />}
				{type === 'icon' && props.icon}
			</Leading>
			{props.label}
		</Container>
	);
}
